import React, { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Robot, PaperPlaneRight, User } from "@phosphor-icons/react";

const SUGGESTIONS = [
  "Comment créer mon premier concert ?",
  "Mon micro ne fonctionne pas, que faire ?",
  "Quand suis-je payé après un concert payant ?",
  "Comment construire ma fanbase ?",
];

const AISupport = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([
    { id: "welcome", role: "assistant", text: "Bonjour 👋 Je suis Echo, l'IA d'XooKmédia. Posez-moi vos questions sur la plateforme, les concerts, le paiement ou les bugs !" },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), [messages]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || sending) return;
    const userMsgId = `u-${Date.now()}`;
    setMessages((p) => [...p, { id: userMsgId, role: "user", text: q }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/ai/support", { message: q, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((p) => [...p, { id: `a-${Date.now()}`, role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((p) => [...p, { id: `err-${Date.now()}`, role: "assistant", text: "Désolé, une erreur s'est produite. Réessayez dans un instant." }]);
    } finally {
      setSending(false);
    }
  };

  if (!user) return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <p>Connectez-vous pour discuter avec Echo.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E11D48] to-[#F59E0B] flex items-center justify-center">
            <Robot weight="fill" size={26} />
          </div>
          <div>
            <h1 className="font-display font-black text-3xl tracking-tight">Echo · Assistant IA</h1>
            <p className="text-zinc-400 text-sm">Réponses 24/7 sur l'app, les bugs, la fanbase.</p>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0f0f10] flex flex-col h-[60vh]">
          <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="ai-chat-messages">
            {messages.map((m) => (
              <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-white/10" : "bg-[#E11D48]/20"}`}>
                  {m.role === "user" ? <User size={18} /> : <Robot size={18} className="text-[#E11D48]" />}
                </div>
                <div className={`px-4 py-3 rounded-2xl text-sm max-w-[80%] leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#E11D48] text-white" : "bg-black/50 border border-white/5 text-zinc-200"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#E11D48]/20 flex items-center justify-center"><Robot size={18} className="text-[#E11D48] animate-pulse" /></div>
                <div className="px-4 py-3 rounded-2xl bg-black/50 border border-white/5 text-sm text-zinc-400">Echo réfléchit...</div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {messages.length <= 1 && (
            <div className="px-6 pb-4 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid={`ai-suggestion-${s.slice(0,10)}`}
                  className="text-xs px-3 py-1.5 rounded-full border border-white/10 hover:border-[#E11D48] hover:text-[#E11D48] text-zinc-400 transition-colors"
                  onClick={() => send(s)}>
                  {s}
                </button>
              ))}
            </div>
          )}

          <div className="p-4 border-t border-white/5 flex gap-2">
            <Input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Posez votre question..."
              className="bg-black/40 border-white/10 text-white" />
            <Button data-testid="ai-send" onClick={() => send()} disabled={sending} className="bg-[#E11D48] hover:bg-[#BE123C]">
              <PaperPlaneRight weight="fill" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AISupport;
